import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { updateData, deleteData, createData, regenerateApproval, socialPublish, regenerateItemImage, libraryList, libraryUpload, autopilotStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, PencilSimple, ArrowsClockwise, CheckCircle, XCircle, Rocket, Copy, Trash,
  Robot, CalendarBlank, Clock, Target, EnvelopeSimple, ChatText, Article,
  FacebookLogo, InstagramLogo, Spinner, Image as ImageIcon, UploadSimple, Images } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: "bg-amber-500/15 text-amber-300", approved: "bg-emerald-500/15 text-emerald-300",
  scheduled: "bg-sky-500/15 text-sky-300", published: "bg-[#6B3DFF]/15 text-[#A56CFF]",
  failed: "bg-red-500/15 text-red-300", draft: "bg-white/10 text-[#9a9a9a]", rejected: "bg-red-500/15 text-red-300",
};
const STATUS_EMOJI = { pending: "🟡", approved: "✅", scheduled: "🟢", published: "🚀", failed: "🔴", rejected: "⛔", draft: "⚪" };
export const publishDate = (item) => {
  if (!item?.scheduled_at) return null;
  const d = new Date(`${item.scheduled_at}T${item.meta?.post_time || "09:00"}:00Z`);
  return isNaN(d) ? null : d;
};
export const fmtPublish = (d) => {
  if (!d) return { date: "Not scheduled", time: "", tz: "" };
  const tz = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "";
  return {
    date: d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    tz,
  };
};
export const countdown = (d) => {
  if (!d) return "";
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return "Due now";
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (days > 0) return `in ${days} day${days > 1 ? "s" : ""} ${hours} hour${hours !== 1 ? "s" : ""}`;
  if (hours > 0) return `in ${hours} hour${hours > 1 ? "s" : ""} ${m} minute${m !== 1 ? "s" : ""}`;
  return `in ${m} minute${m !== 1 ? "s" : ""}`;
};
const PLATFORM_ICON = { Facebook: FacebookLogo, Instagram: InstagramLogo, Email: EnvelopeSimple, SMS: ChatText, Blog: Article };
const API = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (it) => it.image_url || (it.image_path ? `${API}${it.image_path.startsWith("/") ? "" : "/"}${it.image_path}` : null);

export default function ItemDetailSheet({ item, onClose, queryKey }) {
  const qc = useQueryClient();
  const { data: apStatus } = useQuery({ queryKey: ["autopilot", item?.client_id], queryFn: () => autopilotStatus(item.client_id), enabled: !!item?.client_id });
  const autopilotState = apStatus?.state;
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState("");
  const [newDate, setNewDate] = useState("");
  const [hh, setHh] = useState("9");
  const [mm, setMm] = useState("00");
  const [ampm, setAmpm] = useState("AM");
  const [tick, setTick] = useState(1);
  const [libOpen, setLibOpen] = useState(false);
  const [libItems, setLibItems] = useState(null);
  const fileRef = React.useRef(null);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    setEditing(false); setBody(item?.body || "");
    const d = publishDate(item);
    if (d) {
      setNewDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      const h = d.getHours();
      setHh(String(((h + 11) % 12) + 1)); setMm(String(d.getMinutes()).padStart(2, "0")); setAmpm(h >= 12 ? "PM" : "AM");
    } else { setNewDate(item?.scheduled_at || ""); setHh("9"); setMm("00"); setAmpm("AM"); }
  }, [item?.id]); // eslint-disable-line
  const inv = () => qc.invalidateQueries({ queryKey });

  const patch = useMutation({
    mutationFn: (p) => updateData("approvals", item.id, p),
    onSuccess: inv,
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const regen = useMutation({
    mutationFn: () => regenerateApproval(item.id),
    onSuccess: () => { inv(); toast.success("Regenerated with AI"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regenerate failed"),
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (item.type === "social" && (item.meta?.social || item.post_type === "facebook_post")) {
        return socialPublish(item.id);
      }
      return updateData("approvals", item.id, { status: "published", published_at: new Date().toISOString().slice(0, 10) });
    },
    onSuccess: (r) => { inv(); toast.success(r?.test_mode ? "Published (TEST MODE — simulated)" : "Published ✓"); onClose(); },
    onError: (e) => { inv(); toast.error(e?.response?.data?.detail || "Publish failed"); },
  });
  const dup = useMutation({
    mutationFn: () => {
      const { id, _id, history, comments, publish_result, platform_post_id, platform_post_url, ...rest } = item;
      return createData("approvals", { ...rest, title: `${item.title} (copy)`, status: "pending",
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(), history: [], comments: [] });
    },
    onSuccess: () => { inv(); toast.success("Duplicated — the copy is in Pending Approval"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Duplicate failed"),
  });
  const del = useMutation({
    mutationFn: () => deleteData("approvals", item.id),
    onSuccess: () => { inv(); toast.success("Deleted"); onClose(); },
  });
  const regenImage = useMutation({
    mutationFn: () => regenerateItemImage(item.id),
    onSuccess: () => { inv(); toast.success("New image generated — caption & schedule unchanged"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Image generation failed"),
  });
  const uploadImage = useMutation({
    mutationFn: async (file) => {
      const doc = await libraryUpload(item.client_id, file);
      const src = doc.media_url || doc.image_path;
      await updateData("approvals", item.id, { image_path: src, image_url: "" });
    },
    onSuccess: () => { inv(); toast.success("Replacement image attached"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const pickFromLibrary = useMutation({
    mutationFn: (src) => updateData("approvals", item.id, { image_path: src, image_url: "" }),
    onSuccess: () => { inv(); setLibOpen(false); toast.success("Image attached from your library"); },
  });
  const openLibrary = async () => {
    setLibOpen(true);
    if (libItems === null) {
      try {
        const docs = await libraryList(item.client_id);
        setLibItems(docs.filter((d) => (d.media_type || "image") === "image" && (d.image_path || d.media_url)));
      } catch { setLibItems([]); }
    }
  };

  if (!item) return null;
  const PIcon = PLATFORM_ICON[item.platform] || Article;
  const objective = (item.meta?.objective || "").replace(/_/g, " ");
  const contentType = (item.meta?.content_type || "").replace(/_/g, " ");
  const pd = publishDate(item);
  const pf = fmtPublish(pd);
  const img = imgSrc(item);
  const isEmail = item.type === "email";
  const isSms = item.type === "sms";
  const isBlog = item.type === "blog";

  const Act = ({ testid, icon: Icon, label, onClick, cls = "", busy = false, disabled = false }) => (
    <button data-testid={testid} onClick={onClick} disabled={busy || disabled}
      className={`h-9 rounded-lg px-3 flex items-center gap-1.5 text-xs font-semibold border border-white/10 transition-colors hover:bg-white/8 disabled:opacity-40 ${cls}`}>
      {busy ? <Spinner size={13} className="animate-spin" /> : <Icon size={13} weight="fill" />} {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="item-detail-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm demo-fade" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-[#10101A] border-l border-white/10 shadow-2xl overflow-y-auto"
        style={{ animation: "sheet-in .3s cubic-bezier(0.22,1,0.36,1) both" }}>
        <div className="sticky top-0 z-10 bg-[#10101A]/95 backdrop-blur border-b border-white/8 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold rounded-full px-2.5 py-1 ${STATUS_BADGE[item.status] || "bg-white/10 text-white"}`} data-testid="detail-status">
                {STATUS_EMOJI[item.status] || ""} {item.status?.toUpperCase()}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-[#9a9a9a]"><PIcon size={13} weight="fill" /> {item.platform}</span>
            </div>
            <h2 className="mt-2 font-head font-bold text-lg leading-snug" data-testid="detail-title">{item.title}</h2>
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] brand-text font-semibold" data-testid="detail-created-by">
              <Robot size={13} weight="duotone" /> Created by {item.created_by || "your Vozelto AI Team"}
            </div>
          </div>
          <button data-testid="detail-close" onClick={onClose} className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-[#9a9a9a] hover:text-white hover:bg-white/8"><X size={16} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-2.5 text-xs">
            <div className="rounded-lg bg-white/[0.03] border border-white/8 p-2.5">
              <div className="label-caps text-[9px] flex items-center gap-1"><CalendarBlank size={11} /> Publish Time</div>
              <div className="mt-1 text-white font-semibold" data-testid="detail-scheduled">📅 {pf.date}</div>
              {pf.time && <div className="text-[#cfcfcf] mt-0.5" data-testid="detail-scheduled-time">🕒 {pf.time} <span className="text-[#7a7a7a]">🌎 {pf.tz}</span></div>}
              {item.status === "scheduled" && pd && (
                <div className="mt-1.5 text-[11px] text-sky-300 font-semibold" data-testid="detail-countdown">
                  <Clock size={11} className="inline mb-0.5 mr-1" />Publishes {tick && countdown(pd)}
                </div>
              )}
            </div>
            <div className="rounded-lg bg-white/[0.03] border border-white/8 p-2.5">
              <div className="label-caps text-[9px] flex items-center gap-1"><Target size={11} /> Objective</div>
              <div className="mt-1 text-white font-semibold capitalize" data-testid="detail-objective">{objective || contentType || "—"}</div>
            </div>
          </div>
          {contentType && <div className="text-[11px] text-[#9a9a9a]">Content type: <span className="text-[#cfcfcf] capitalize">{contentType}</span></div>}

          {item.status === "failed" && item.publish_result?.error && (
            <div className="rounded-xl border border-red-500/25 bg-red-500/[0.06] p-3" data-testid="detail-publish-error">
              <div className="label-caps text-[10px] text-red-300">Publish failed{item.meta?.publish_attempts ? ` — ${item.meta.publish_attempts} attempt(s)` : ""}</div>
              <div className="mt-1 text-xs text-red-200 break-words">{item.publish_result.error}</div>
            </div>
          )}
          {item.status === "published" && (item.platform_post_url || item.publish_result?.url) && (
            <a href={item.platform_post_url || item.publish_result?.url} target="_blank" rel="noreferrer"
              className="block rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3 hover:bg-emerald-500/[0.1]" data-testid="detail-permalink">
              <div className="label-caps text-[10px] text-emerald-300">Published — view live post ↗</div>
              <div className="mt-1 text-xs text-emerald-200 truncate">{item.platform_post_url || item.publish_result?.url}</div>
            </a>
          )}
          {item.status === "scheduled" && autopilotState && autopilotState !== "active" && (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-xs text-amber-300" data-testid="detail-autopilot-off">
              ⚠️ Auto Pilot is {autopilotState === "paused" ? "paused" : "off"} — this will not publish automatically. Turn it on from the Dashboard, or use Publish Now.
            </div>
          )}

          {item.type === "social" ? (
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3" data-testid="detail-image-manager">
              <div className="flex items-center justify-between mb-2">
                <div className="label-caps text-[10px]">{item.platform === "Instagram" ? "Instagram image (required)" : "Post image"}</div>
                {item.meta?.image_status === "generating" && <span className="text-[10px] text-amber-300 animate-pulse">Generating…</span>}
                {item.meta?.image_status === "failed" && <span className="text-[10px] text-red-400">Last generation failed</span>}
              </div>
              {img ? (
                <img src={img} alt={item.title} className="w-full rounded-lg border border-white/8" data-testid="detail-image" loading="lazy" />
              ) : (
                <div className="rounded-lg border border-dashed border-white/15 py-8 text-center" data-testid="detail-image-missing">
                  <ImageIcon size={26} className="mx-auto text-[#5a5a5a] mb-1.5" />
                  <div className="text-xs text-[#7a7a7a]">
                    {item.platform === "Instagram" ? "No image yet — Instagram requires one. Auto Pilot will generate it before publishing, or attach one now." : "No image attached (optional for Facebook)."}
                  </div>
                </div>
              )}
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Act testid="detail-regen-image" icon={ArrowsClockwise} label={img ? "Regenerate Image" : "Generate Image"} onClick={() => regenImage.mutate()} busy={regenImage.isPending} cls="text-[#A56CFF] border-[#6B3DFF]/30" />
                <Act testid="detail-upload-image" icon={UploadSimple} label="Upload Replacement" onClick={() => fileRef.current?.click()} busy={uploadImage.isPending} cls="text-[#cfcfcf]" />
                <Act testid="detail-library-image" icon={Images} label="Choose from Library" onClick={openLibrary} cls="text-[#cfcfcf]" />
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="detail-image-file-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage.mutate(f); e.target.value = ""; }} />
              </div>
              <div className="mt-1.5 text-[10px] text-[#7a7a7a]">Replacing the image keeps your caption, hashtags and publish time unchanged. Auto Pilot always publishes the image shown here.</div>
              {libOpen && (
                <div className="mt-3 rounded-lg border border-white/10 bg-[#0a0a0a] p-3" data-testid="detail-library-picker">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold">AI Image Library</span>
                    <button onClick={() => setLibOpen(false)} className="text-[#7a7a7a] hover:text-white"><X size={13} /></button>
                  </div>
                  {libItems === null ? <div className="text-xs text-[#7a7a7a] py-4 text-center">Loading…</div>
                    : libItems.length === 0 ? <div className="text-xs text-[#7a7a7a] py-4 text-center">No images in your library yet.</div>
                      : (
                        <div className="grid grid-cols-4 gap-1.5 max-h-56 overflow-y-auto">
                          {libItems.slice(0, 40).map((d) => {
                            const src = d.image_path || d.media_url;
                            const full = src.startsWith("http") ? src : `${API}${src.startsWith("/") ? "" : "/"}${src}`;
                            return (
                              <button key={d.id} data-testid={`library-pick-${d.id}`} onClick={() => pickFromLibrary.mutate(src)}
                                className="rounded-lg overflow-hidden border border-white/8 hover:border-[#6B3DFF]/60 transition-colors aspect-square">
                                <img src={full} alt={d.title || "library"} className="w-full h-full object-cover" loading="lazy" />
                              </button>
                            );
                          })}
                        </div>
                      )}
                </div>
              )}
            </div>
          ) : img && (
            <img src={img} alt={item.title} className="w-full rounded-xl border border-white/8" data-testid="detail-image" loading="lazy" />
          )}

          {isEmail && item.meta?.subject && (
            <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 p-3" data-testid="detail-email-subject">
              <div className="label-caps text-[9px] text-emerald-400">Email subject</div>
              <div className="mt-1 text-sm font-semibold">{item.meta.subject}</div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="label-caps text-[10px]">{isEmail ? "Email body" : isSms ? "SMS text" : isBlog ? "Blog draft preview" : "Post content"}</div>
              {isSms && <span className="text-[10px] text-[#7a7a7a]">{(item.body || "").length} chars</span>}
            </div>
            {editing ? (
              <>
                <Textarea data-testid="detail-edit-body" value={body} onChange={(e) => setBody(e.target.value)} rows={isBlog ? 12 : 7}
                  className="bg-white/5 border-white/10 text-sm" />
                <div className="mt-2 flex gap-2">
                  <Button data-testid="detail-save-edit" size="sm" onClick={() => { patch.mutate({ body }); setEditing(false); toast.success("Saved"); }}
                    className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-8">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setBody(item.body || ""); }} className="text-[#9a9a9a] h-8">Cancel</Button>
                </div>
              </>
            ) : (
              <div data-testid="detail-body" className={`rounded-xl border border-white/8 bg-white/[0.03] p-3.5 text-sm text-[#cfcfcf] leading-relaxed whitespace-pre-wrap ${isBlog ? "max-h-72 overflow-y-auto" : ""}`}>
                {item.body || <span className="text-[#7a7a7a] italic">No content</span>}
              </div>
            )}
            {item.hashtags && !editing && <div className="mt-2 text-xs brand-text break-words" data-testid="detail-hashtags">{item.hashtags}</div>}
          </div>

          {item.meta?.reel_storyboard && (
            <div className="rounded-xl border border-pink-500/25 bg-pink-500/[0.05] p-3.5" data-testid="detail-reel-storyboard">
              <div className="label-caps text-[9px] text-pink-300 mb-2">Reel storyboard — record this, then publish from your phone</div>
              {item.meta.reel_storyboard.hook && <div className="text-sm font-semibold mb-2">Hook: <span className="font-normal text-[#cfcfcf]">{item.meta.reel_storyboard.hook}</span></div>}
              <div className="space-y-1.5">
                {(item.meta.reel_storyboard.scenes || []).map((s, i) => (
                  <div key={i} className="text-xs text-[#cfcfcf] flex gap-2">
                    <span className="shrink-0 h-5 w-5 rounded-full bg-pink-500/20 text-pink-300 text-[10px] font-bold flex items-center justify-center">{s.scene || i + 1}</span>
                    <span>{s.action}{s.text_overlay ? <span className="text-[#9a9a9a]"> — overlay: “{s.text_overlay}”</span> : null}</span>
                  </div>
                ))}
              </div>
              {item.meta.reel_storyboard.audio_suggestion && <div className="mt-2 text-[11px] text-[#9a9a9a]">🎵 Audio: {item.meta.reel_storyboard.audio_suggestion}</div>}
              {item.meta?.requires_video && <div className="mt-2 text-[10px] text-amber-300/90">Auto Pilot won't publish this until you record the video — it stays here waiting for you.</div>}
            </div>
          )}

          {Array.isArray(item.meta?.carousel_slides) && item.meta.carousel_slides.length > 0 && (
            <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-3.5" data-testid="detail-carousel-slides">
              <div className="label-caps text-[9px] text-sky-300 mb-2">Carousel — {item.meta.carousel_slides.length} slides (images come from your Media Library)</div>
              <div className="space-y-1.5">
                {item.meta.carousel_slides.map((s, i) => (
                  <div key={i} className="text-xs text-[#cfcfcf] flex gap-2">
                    <span className="shrink-0 h-5 w-5 rounded-full bg-sky-500/20 text-sky-300 text-[10px] font-bold flex items-center justify-center">{s.slide || i + 1}</span>
                    <span>{s.heading ? <b>{s.heading}: </b> : null}{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3">
            <div className="label-caps text-[9px] mb-2">Publish date & time <span className="normal-case text-[#7a7a7a]">(your local timezone{pf.tz ? ` — ${pf.tz}` : ""})</span></div>
            <div className="flex flex-wrap gap-2 items-center">
              <input type="date" data-testid="detail-date-input" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                className="h-9 rounded-lg bg-white/5 border border-white/10 px-3 text-xs" />
              <select data-testid="detail-hour-select" value={hh} onChange={(e) => setHh(e.target.value)}
                className="h-9 rounded-lg bg-white/5 border border-white/10 px-2 text-xs">
                {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((h) => <option key={h} value={h} className="bg-[#161622]">{h}</option>)}
              </select>
              <span className="text-[#7a7a7a] text-sm">:</span>
              <select data-testid="detail-minute-select" value={mm} onChange={(e) => setMm(e.target.value)}
                className="h-9 rounded-lg bg-white/5 border border-white/10 px-2 text-xs">
                {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => <option key={m} value={m} className="bg-[#161622]">{m}</option>)}
              </select>
              <div className="flex rounded-lg bg-white/5 border border-white/10 p-0.5">
                {["AM", "PM"].map((p) => (
                  <button key={p} data-testid={`detail-ampm-${p.toLowerCase()}`} onClick={() => setAmpm(p)}
                    className={`h-8 px-2.5 rounded-md text-xs font-semibold transition-colors ${ampm === p ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a] hover:text-white"}`}>{p}</button>
                ))}
              </div>
              <Button data-testid="detail-update-schedule" size="sm" disabled={!newDate || patch.isPending}
                onClick={() => {
                  const h24 = (Number(hh) % 12) + (ampm === "PM" ? 12 : 0);
                  const local = new Date(`${newDate}T${String(h24).padStart(2, "0")}:${mm}:00`);
                  if (isNaN(local)) return toast.error("Invalid date/time");
                  const utcDate = local.toISOString().slice(0, 10);
                  const utcTime = local.toISOString().slice(11, 16);
                  patch.mutate({ scheduled_at: utcDate, "meta.post_time": utcTime });
                  toast.success(`Publish time updated — ${local.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${local.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} ${pf.tz || ""}`);
                }}
                className="bg-white/10 hover:bg-white/15 text-white h-9 ml-auto">Update Schedule</Button>
            </div>
            <div className="mt-2 text-[10px] text-[#7a7a7a]">Auto Pilot and the Marketing Calendar update instantly — the item publishes at the new time.</div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1 pb-6" data-testid="detail-actions">
            <Act testid="detail-edit" icon={PencilSimple} label="Edit" onClick={() => setEditing(true)} cls="text-[#cfcfcf]" />
            <Act testid="detail-regenerate" icon={ArrowsClockwise} label="Regenerate" onClick={() => regen.mutate()} busy={regen.isPending} cls="text-[#cfcfcf]" />
            {item.status !== "published" && item.status !== "scheduled" && (
              <Act testid="detail-approve" icon={CheckCircle} label="Approve" onClick={() => { patch.mutate({ status: "approved" }); toast.success(item.meta?.auto_schedule ? "Approved & scheduled" : "Approved"); }} cls="text-emerald-400 border-emerald-500/25" />
            )}
            {item.status !== "published" && (
              <Act testid="detail-reject" icon={XCircle} label="Reject" onClick={() => { patch.mutate({ status: "rejected" }); toast.success("Rejected"); onClose(); }} cls="text-red-400 border-red-500/25" />
            )}
            {item.status !== "published" && (
              <Act testid="detail-publish" icon={Rocket} label="Publish Now" onClick={() => publish.mutate()} busy={publish.isPending} cls="text-[#A56CFF] border-[#6B3DFF]/30" />
            )}
            <Act testid="detail-duplicate" icon={Copy} label="Duplicate" onClick={() => dup.mutate()} busy={dup.isPending} cls="text-[#cfcfcf]" />
            <Act testid="detail-delete" icon={Trash} label="Delete" onClick={() => del.mutate()} busy={del.isPending} cls="text-[#9a9a9a]" />
          </div>
        </div>
      </div>
    </div>
  );
}
